// Page publique d'export des participants par séance : un client revient ici avec le lien reçu de
// l'équipe Wilout pour télécharger, en CSV, les participants de chaque séance, d'une journée ou de
// toutes à la fois. Aucune authentification : le lien (token révocable et daté) en tient lieu.
// Lecture seule. Mise en page : maquette « Export Participants Public » (PublicSessionExportPage.css).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ChevronDown, ChevronRight, CircleCheck, Download, FileSpreadsheet, Lock, Search, ShieldCheck, TriangleAlert, X,
} from 'lucide-react';
import { getPublicSessionExport, getPublicSessionParticipants, getPublicAllSessionsParticipants } from '../../api/api';
import { downloadSessionCsv, downloadAllSessionsCsv } from '../event-details/sessionExportCsv';
import { downloadSessionXlsx, downloadAllSessionsXlsx } from '../event-details/sessionExportXlsx';
import { scanStatus } from '../event-details/sessionExportData';
import wiloutLogo from '../../images/wilout-logo-full.svg';
import './PublicSessionExportPage.css';

const NO_DATE = 'Sans date';
const TOAST_MS = 2600;

// Formats d'export : CSV (Excel FR, UTF-8) ou Excel (.xlsx) avec le statut de scan en couleur.
const FORMATS = {
  csv: { label: 'CSV', article: 'le CSV', session: downloadSessionCsv, all: downloadAllSessionsCsv, footer: 'Exports au format CSV (UTF-8, séparateur point-virgule).' },
  xlsx: { label: 'Excel', article: "l'Excel", session: downloadSessionXlsx, all: downloadAllSessionsXlsx, footer: 'Exports au format Excel (.xlsx), statut de scan en couleur.' },
};

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
const plural = (n, one, many = `${one}s`) => `${fmt(n)} ${n > 1 ? many : one}`;
const cap = (text) => text.charAt(0).toUpperCase() + text.slice(1);

// "JJ/MM/AAAA HH:mm" → jour ("JJ/MM/AAAA"), date, heure et clé de tri ; une date illisible va dans « Sans date ».
const parseStart = (startDate) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})(?: (\d{2}):(\d{2}))?/.exec(startDate || '');
  if (!m) return { key: NO_DATE, date: null, time: '', sortKey: '9' };
  const [, dd, mm, yyyy, hh = '', mi = ''] = m;
  return {
    key: `${dd}/${mm}/${yyyy}`,
    date: new Date(Number(yyyy), Number(mm) - 1, Number(dd)),
    time: hh ? `${hh}:${mi}` : '',
    sortKey: `${yyyy}${mm}${dd}${hh}${mi}`,
  };
};

const dayLabel = (day) => (day.date ? cap(day.date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' })) : NO_DATE);
const dayChipLabel = (day) => (day.date ? `${cap(day.date.toLocaleDateString('fr-FR', { weekday: 'short' }))} ${day.key.slice(0, 5)}` : NO_DATE);

const rangeLabel = (days) => {
  const dated = days.filter((d) => d.date);
  if (!dated.length) return '';
  const first = dated[0].date;
  const last = dated[dated.length - 1].date;
  const short = (d) => d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  if (first.getTime() === last.getTime()) return `Le ${short(first)} ${first.getFullYear()}`;
  const sameMonth = first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear();
  return `Du ${sameMonth ? first.getDate() : short(first)} au ${short(last)} ${last.getFullYear()}`;
};

const linkErrorMessage = (err, fallback) => (err?.response?.status === 404 ? 'Ce lien a expiré ou a été désactivé.' : fallback);

const Spinner = () => <span className="pse-spinner" aria-hidden="true" />;

const Callout = ({ tone, icon: Icon, role, children }) => (
  <div className={`pse-callout pse-callout--${tone}`} role={role}><Icon size={16} /><span>{children}</span></div>
);

const PageShell = ({ expiresAt, children }) => (
  <div className="pse-page">
    <header className="pse-topbar">
      <div className="pse-topbar__inner">
        <img className="pse-topbar__logo" src={wiloutLogo} alt="Wilout" />
        {expiresAt && (
          <span className="pse-topbar__secure"><Lock size={12} />Lien sécurisé · expire le {new Date(expiresAt).toLocaleDateString('fr-FR')}</span>
        )}
      </div>
    </header>
    <main className="pse-main">{children}</main>
  </div>
);

// Stock total de la séance (null = illimité, absent = non fourni par l'API) et dépassement : places vendues au-delà du stock.
const stockLabel = (stock) => (stock === null ? 'Stock illimité' : `Stock total : ${plural(stock, 'place')}`);
const overStock = (session) => (session.stock != null && session.places > session.stock ? session.places - session.stock : 0);

const SessionCard = ({ session, format, max, busy, disabled, onDownload, onOpen }) => {
  const has = session.participants > 0;
  const over = overStock(session);
  return (
    <article className={`pse-card pse-card--clickable${has ? '' : ' pse-card--empty'}${over ? ' pse-card--over' : ''}`} aria-label={session.nom}>
      <div className="pse-card__top">
        <span className="pse-card__time">{session.time || '—'}</span>
        <span className="pse-card__count">{plural(session.participants, 'participant')}</span>
      </div>
      <button type="button" className="pse-card__name pse-card__open" aria-label={`Voir les détails de ${session.nom}`} onClick={onOpen}>{session.nom}</button>
      {session.stock !== undefined && (
        <span className="pse-card__stock">
          {stockLabel(session.stock)}
          {over > 0 && <span className="pse-card__over"><TriangleAlert size={12} aria-hidden="true" />Dépassé de {fmt(over)}</span>}
        </span>
      )}
      <span className="pse-card__bar"><span style={{ width: `${Math.round((session.participants / max) * 100)}%` }} /></span>
      <button type="button" className="pse-btn-csv" aria-label={`Télécharger ${format.article} de ${session.nom}`} disabled={disabled || !has} onClick={onDownload}>
        {busy ? <Spinner /> : <FileSpreadsheet size={14} />}Télécharger {format.label}
      </button>
    </article>
  );
};

// Pastille colorée (vert / rouge / ambre) : l'icône et le texte portent aussi le sens, pas la couleur seule.
const ScanBadge = ({ row }) => {
  const { tone, label } = scanStatus(row);
  return (
    <span className={`pse-scan pse-scan--${tone}`}>
      {tone === 'yes' && <CircleCheck size={13} aria-hidden="true" />}
      {tone === 'no' && <X size={13} aria-hidden="true" />}
      {label}
    </span>
  );
};

const sessionWhen = (session) => [session.key !== NO_DATE ? session.key : '', session.time].filter(Boolean).join(' · ');

// Modale de la page : fermeture par Échap, clic sur le fond ou la croix.
const Modal = ({ label, title, meta, onClose, children }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="pse-modal__backdrop" role="presentation" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pse-modal" role="dialog" aria-modal="true" aria-label={label}>
        <header className="pse-modal__head">
          <div>
            <h2 className="pse-modal__title">{title}</h2>
            <span className="pse-modal__meta">{meta}</span>
          </div>
          <button type="button" className="pse-modal__close" aria-label="Fermer" onClick={onClose}><X size={18} /></button>
        </header>
        <div className="pse-modal__body">{children}</div>
      </div>
    </div>
  );
};

// Détails d'une séance : liste nom / prénom / email / scan des participants (chargée à l'ouverture).
const SessionDetailsModal = ({ session, state, onClose }) => (
  <Modal
    label={`Détails de ${session.nom}`}
    title={session.nom}
    meta={[sessionWhen(session), plural(session.participants, 'participant'), session.stock !== undefined ? stockLabel(session.stock) : ''].filter(Boolean).join(' · ')}
    onClose={onClose}
  >
    {state?.loading && <div className="pse-loading" role="status" aria-label="Chargement"><Spinner /></div>}
    {state?.error && <Callout tone="danger" icon={TriangleAlert} role="alert">{state.error}</Callout>}
    {state?.result && state.result.rows.length === 0 && <div className="pse-empty">Aucun participant pour cette séance.</div>}
    {state?.result?.rows.length > 0 && (
      <table className="pse-table">
        <thead><tr><th>Nom</th><th>Prénom</th><th>Email</th><th>Scanné</th></tr></thead>
        <tbody>
          {state.result.rows.map((r, i) => (
            <tr key={`${r.codeBillet || r.commande || ''}-${i}`}>
              <td>{r.nom}</td><td>{r.prenom}</td><td>{r.email}</td>
              <td><ScanBadge row={r} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </Modal>
);

// Séances dont les places vendues dépassent le stock total, dans l'ordre chronologique.
const OverStockModal = ({ sessions, onClose }) => (
  <Modal
    label="Séances en dépassement de stock"
    title="Séances en dépassement de stock"
    meta={sessions.length ? `${plural(sessions.length, 'séance')} · places vendues au-delà du stock total` : 'Aucune séance ne dépasse son stock.'}
    onClose={onClose}
  >
    {sessions.length === 0 ? (
      <div className="pse-empty">Toutes les séances sont dans leur stock.</div>
    ) : (
      <table className="pse-table">
        <thead><tr><th>Séance</th><th>Date</th><th>Vendues</th><th>Stock</th><th>Dépassement</th></tr></thead>
        <tbody>
          {sessions.map((s) => (
            <tr key={s.id}>
              <td>{s.nom}</td><td>{sessionWhen(s) || '—'}</td><td>{fmt(s.places)}</td><td>{fmt(s.stock)}</td>
              <td><span className="pse-scan pse-scan--no">+{fmt(overStock(s))}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </Modal>
);

const PublicSessionExportPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [notice, setNotice] = useState('');
  const [toast, setToast] = useState('');
  const [query, setQuery] = useState('');
  const [hideEmpty, setHideEmpty] = useState(true);
  const [selectedDay, setSelectedDay] = useState('all');
  const [closedDays, setClosedDays] = useState({});
  const [openSession, setOpenSession] = useState(null);
  const [format, setFormat] = useState('csv');
  const [details, setDetails] = useState({});
  const [showOverStock, setShowOverStock] = useState(false);
  const toastTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getPublicSessionExport(token);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.status === 404
            ? 'Ce lien est invalide ou a expiré. Demandez-en un nouveau à votre contact Wilout.'
            : 'Impossible de charger les séances pour le moment. Réessayez dans quelques minutes.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const flash = useCallback((message) => {
    setToast(message);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), TOAST_MS);
  }, []);

  // Séances regroupées par jour (chronologique, « Sans date » en dernier), triées par heure.
  const days = useMemo(() => {
    const byDay = new Map();
    for (const s of data?.sessions || []) {
      const start = parseStart(s.startDate);
      if (!byDay.has(start.key)) byDay.set(start.key, { key: start.key, date: start.date, sessions: [] });
      // `places` absent (ancienne API) : un billet = une place.
      byDay.get(start.key).sessions.push({ ...s, places: s.places ?? s.participants, key: start.key, time: start.time, sortKey: start.sortKey });
    }
    return [...byDay.values()]
      .map((d) => ({
        ...d,
        sessions: d.sessions.sort((a, b) => a.sortKey.localeCompare(b.sortKey)),
        total: d.sessions.reduce((n, s) => n + s.participants, 0),
        filled: d.sessions.filter((s) => s.participants > 0).length,
      }))
      .sort((a, b) => (a.date ? a.date.getTime() : Infinity) - (b.date ? b.date.getTime() : Infinity));
  }, [data]);

  const run = useCallback(async (key, task) => {
    setBusyKey(key);
    setNotice('');
    try {
      await task();
    } catch (err) {
      console.error('Erreur export public séances:', err);
      setNotice(linkErrorMessage(err, 'Le téléchargement a échoué. Réessayez dans quelques instants.'));
    } finally {
      setBusyKey(null);
    }
  }, []);

  // Les participants sont chargés une fois par séance (route publique limitée à 20 requêtes/minute),
  // puis réutilisés par l'export CSV de la séance.
  const openDetails = async (session) => {
    setOpenSession(session);
    if (details[session.id]?.result || details[session.id]?.loading) return;
    setDetails((prev) => ({ ...prev, [session.id]: { loading: true } }));
    try {
      const result = await getPublicSessionParticipants(token, session.id);
      setDetails((prev) => ({ ...prev, [session.id]: { result } }));
    } catch (err) {
      setDetails((prev) => ({
        ...prev,
        [session.id]: { error: linkErrorMessage(err, 'Impossible de charger les participants. Réessayez dans quelques instants.') },
      }));
    }
  };

  const warnTruncated = (count) => setNotice(`Export limité aux ${fmt(count)} premières lignes.`);

  const exportSession = (session) => run(session.id, async () => {
    const result = details[session.id]?.result || await getPublicSessionParticipants(token, session.id);
    if (!result.rows.length) return setNotice(`Aucun participant pour la séance « ${session.nom} ».`);
    await FORMATS[format].session(result);
    flash(`${session.nom} · ${plural(result.rows.length, 'participant')} ${result.rows.length > 1 ? 'exportés' : 'exporté'}`);
    if (result.truncated) warnTruncated(result.rows.length);
  });

  const exportAll = () => run('all', async () => {
    const result = await getPublicAllSessionsParticipants(token);
    const count = await FORMATS[format].all(result);
    if (!count) return setNotice('Aucun participant à exporter.');
    flash(`Export complet · ${plural(count, 'participant')}`);
    if (result.truncated) warnTruncated(count);
  });

  // Une seule requête (l'export complet) filtrée sur la journée : la route publique est limitée à
  // 20 requêtes par minute, un appel par séance l'épuiserait.
  const exportDay = (day) => run(`day:${day.key}`, async () => {
    const result = await getPublicAllSessionsParticipants(token);
    const sessions = result.sessions.filter(({ session }) => parseStart(session.startDate).key === day.key);
    const count = await FORMATS[format].all({ ...result, sessions }, `jour_${day.key}`);
    if (!count) return setNotice(`Aucun participant pour le ${dayLabel(day).toLowerCase()}.`);
    flash(`Export du ${dayLabel(day).toLowerCase()} · ${plural(count, 'participant')}`);
    // La limite porte sur l'export complet : la journée peut être complète, ou non si elle en a subi la coupe.
    if (result.truncated) setNotice("Export potentiellement incomplet : la limite d'export a été atteinte. Contactez votre contact Wilout.");
  });

  if (loading) {
    return <PageShell><div className="pse-loading" role="status" aria-label="Chargement"><Spinner /></div></PageShell>;
  }
  if (error) {
    return (
      <PageShell>
        <Callout tone="danger" icon={TriangleAlert} role="alert">{error}</Callout>
      </PageShell>
    );
  }

  const totalParticipants = days.reduce((n, d) => n + d.total, 0);
  const max = Math.max(1, ...data.sessions.map((s) => s.participants));
  const q = query.trim().toLowerCase();
  const visibleDays = days
    .filter((d) => selectedDay === 'all' || selectedDay === d.key)
    .map((d) => {
      const matched = d.sessions.filter((s) => !q || s.nom.toLowerCase().includes(q) || s.time.includes(q));
      const shown = hideEmpty ? matched.filter((s) => s.participants > 0) : matched;
      return { ...d, matched, shown, hidden: matched.length - shown.length };
    })
    .filter((d) => !q || d.matched.length > 0);

  const overStockSessions = days.flatMap((d) => d.sessions).filter((s) => overStock(s) > 0);
  const hasStock = data.sessions.some((s) => s.stock !== undefined);

  const chips = [{ key: 'all', label: 'Tous les jours', count: totalParticipants }, ...days.map((d) => ({ key: d.key, label: dayChipLabel(d), count: d.total }))];

  return (
    <PageShell expiresAt={data.expiresAt}>
      <section className="pse-hero">
        <div className="pse-hero__text">
          <span className="pse-eyebrow">Export des participants</span>
          <h1 className="pse-title">{data.eventName || 'Événement'}</h1>
          <div className="pse-stats" role="group" aria-label="Résumé de l'export">
            <span><strong>{fmt(totalParticipants)}</strong> participants</span>
            <span><strong>{fmt(data.sessions.length)}</strong> séances</span>
            {rangeLabel(days) && <span>{rangeLabel(days)}</span>}
          </div>
        </div>
        {data.sessions.length > 0 && (
          <button type="button" className="pse-btn-primary" disabled={!!busyKey || totalParticipants === 0} onClick={exportAll}>
            {busyKey === 'all' ? <Spinner /> : <Download size={18} />}Tout exporter
          </button>
        )}
      </section>

      <Callout tone="info" icon={ShieldCheck}>
        Ces fichiers contiennent des données personnelles (nom, prénom, email). Ne transférez pas ce lien et supprimez les fichiers après usage.
      </Callout>

      {notice && <Callout tone="warning" icon={TriangleAlert} role="alert">{notice}</Callout>}

      {data.sessions.length === 0 ? (
        <div className="pse-empty">Aucune séance à exporter pour le moment.</div>
      ) : (
        <>
          <section className="pse-filters">
            <div className="pse-filters__row">
              <label className="pse-search">
                <Search size={14} />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher une séance ou un horaire" aria-label="Rechercher une séance ou un horaire" />
              </label>
              <button type="button" role="switch" aria-checked={hideEmpty} className="pse-switch" onClick={() => setHideEmpty((v) => !v)}>
                <span className="pse-switch__track"><span className="pse-switch__knob" /></span>
                Masquer les séances vides
              </button>
            </div>
            <div className="pse-chips" role="group" aria-label="Format d'export">
              {Object.entries(FORMATS).map(([key, f]) => (
                <button key={key} type="button" className="pse-chip" aria-pressed={format === key} onClick={() => setFormat(key)}>
                  <span>Format {f.label}</span>
                </button>
              ))}
            </div>
            <div className="pse-chips" role="group" aria-label="Filtrer par jour">
              {chips.map((chip) => (
                <button key={chip.key} type="button" className="pse-chip" aria-pressed={selectedDay === chip.key} onClick={() => setSelectedDay(chip.key)}>
                  <span>{chip.label}</span>
                  <span className="pse-chip__count">{fmt(chip.count)}</span>
                </button>
              ))}
            </div>
          </section>

          {visibleDays.length === 0 && <div className="pse-empty">Aucune séance ne correspond à la recherche.</div>}

          {visibleDays.map((day) => {
            const open = !closedDays[day.key];
            return (
              <section key={day.key} className="pse-day" aria-label={dayLabel(day)}>
                <header className="pse-day__head">
                  <button
                    type="button"
                    className="pse-day__toggle"
                    aria-expanded={open}
                    onClick={() => setClosedDays((prev) => ({ ...prev, [day.key]: open }))}
                  >
                    {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <span>
                      <span className="pse-day__label">{dayLabel(day)}</span>
                      <span className="pse-day__meta">{plural(day.total, 'participant')} · {day.filled}/{day.sessions.length} séances avec inscrits</span>
                    </span>
                  </button>
                  <button type="button" className="pse-btn-outline" aria-label={`Exporter la journée : ${dayLabel(day)}`} disabled={!!busyKey || day.total === 0} onClick={() => exportDay(day)}>
                    {busyKey === `day:${day.key}` ? <Spinner /> : <Download size={14} />}Exporter la journée
                  </button>
                </header>
                {open && (
                  <>
                    {day.shown.length > 0 && (
                      <div className="pse-grid">
                        {day.shown.map((s) => (
                          <SessionCard
                            key={s.id}
                            session={s}
                            format={FORMATS[format]}
                            max={max}
                            busy={busyKey === s.id}
                            disabled={!!busyKey}
                            onDownload={() => exportSession(s)}
                            onOpen={() => openDetails(s)}
                          />
                        ))}
                      </div>
                    )}
                    {day.hidden > 0 && (
                      <button type="button" className="pse-hidden-more" onClick={() => setHideEmpty(false)}>
                        {plural(day.hidden, 'séance')} sans participant {day.hidden > 1 ? 'masquées' : 'masquée'} · Afficher
                      </button>
                    )}
                  </>
                )}
              </section>
            );
          })}
        </>
      )}

      <footer className="pse-footer">
        {FORMATS[format].footer}
        {hasStock && (
          <button type="button" className="pse-footer__link" onClick={() => setShowOverStock(true)}>
            Voir les séances en dépassement de stock ({fmt(overStockSessions.length)})
          </button>
        )}
      </footer>

      {showOverStock && <OverStockModal sessions={overStockSessions} onClose={() => setShowOverStock(false)} />}
      {openSession && <SessionDetailsModal session={openSession} state={details[openSession.id]} onClose={() => setOpenSession(null)} />}

      {/* Zone live toujours montée : un role="status" inséré avec son texte n'est pas annoncé par tous les lecteurs d'écran. */}
      <div role="status" aria-live="polite">
        {toast && <div className="pse-toast"><CircleCheck size={16} /><span>{toast}</span></div>}
      </div>
    </PageShell>
  );
};

export default PublicSessionExportPage;
